import { useState, useCallback, useRef, type KeyboardEvent, type FormEvent } from 'react'
import * as buttonStyles from '@/components/ui/buttonStyles'
import { cn } from '@/utils/cn'
import { usePendo } from '@/hooks/usePendo'

export interface MessageInputProps {
  onSend: (message: string) => void
  disabled?: boolean
  placeholder?: string
  maxLength?: number
  className?: string
}

const customButtonStyles = cn(
  buttonStyles.baseStyles,
  buttonStyles.variantStyles.primary,
  buttonStyles.sizeStyles.md,
  'p-4 absolute bottom-[11px] right-1',
  'focus:ring-offset-0'
)

function ContactNumber() {
  const PHONE_NUMBER = "(855) 360-1317"
  const props = {
    "href": "tel:8553601317",
    "className": "underline text-primary-600 hover:text-primary-700",
    "aria-label": "Call Lantern Care Advocate at " + PHONE_NUMBER,
  }

  return (
    <a {...props}>
      {PHONE_NUMBER}
    </a>
  )
}

function Disclaimer() {
  return (
    <p className="text-sm text-gray-500 italic">
        Generative AI can make mistakes. The Lantern Digital Assistant provides general information about your Lantern benefit(s), not medical advice or final coverage decisions. For confirmation on coverage or other questions, connect with a Care Advocate by calling <ContactNumber></ContactNumber>. <span className="font-bold">If you are experiencing a medical emergency, call 911.</span>
    </p>
  )
}

/**
 * Accessible message input with keyboard shortcuts.
 * Enter to send, Shift+Enter for newline, Escape to clear.
 */
export function MessageInput({
  onSend,
  disabled = false,
  placeholder = 'Your message',
  maxLength = 2500,
  className,
}: MessageInputProps) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { track } = usePendo()

  const charCount = value.length
  const isOverLimit = charCount > maxLength
  const canSend = value.trim().length > 0 && !isOverLimit && !disabled

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [])

  // Handle form submission
  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      if (canSend) {
        track("assistant_message_sent")
        onSend(value.trim())
        setValue('')
        // Reset height after clearing
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto'
        }
        // Keep focus on input
        textareaRef.current?.focus()
      }
    },
    [canSend, onSend, track, value]
  )

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter without shift = send
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (canSend) {
          track("assistant_message_sent")
          onSend(value.trim())
          setValue('')
          if (textareaRef.current) {
            textareaRef.current.style.height = 'auto'
          }
        }
      }

      // Escape = clear input
      if (e.key === 'Escape') {
        track("assistant_message_cleared")
        setValue('')
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto'
        }
      }
    },
    [canSend, onSend, track, value]
  )

  // Handle input change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value)
      adjustHeight()
    },
    [adjustHeight]
  )

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('border-t border-gray-200 bg-white p-1 md:p-4', className)}
    >
      <div className="relative">
        <label htmlFor="chat-input" className="sr-only">
          {placeholder}
        </label>

        <textarea
          ref={textareaRef}
          id="chat-input"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className={cn(
            'overflow-hidden', // don't show the scrollbar, we'll auto-expand instead
            'w-full resize-none rounded-lg border px-4 py-3 pr-20',
            'text-gray-900 placeholder:text-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            'disabled:cursor-not-allowed disabled:bg-gray-50',
            isOverLimit
              ? 'border-error-500 focus:border-error-500 focus:ring-error-500'
              : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
          )}
          aria-invalid={isOverLimit}
        />

        <button
          className={customButtonStyles}
          disabled={!canSend || isOverLimit}
          type="submit"
          aria-label='Send message'
        >
          Send
        </button>
      </div>
      <Disclaimer />
    </form>
  )
}
